import { useEffect, useState } from 'react';
import {
  useRouteError,
  isRouteErrorResponse,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorPanel } from '@/components/ErrorPanel';

const REDIRECT_SECONDS = 5;

/**
 * Per-route error boundary.
 *
 * Wired via React Router's `errorElement` prop (see `App.tsx`). Catches
 * render errors, loader/action errors, and 404s, and renders a "Something
 * went wrong" panel with retry / back-to-browse buttons.
 *
 * Auto-redirects to "/" after 5s when the user is not already on "/" — so
 * stale-deploy chunk-load errors recover on their own. Buttons stay clickable
 * during the countdown; clicking either implicitly cancels via unmount.
 */
export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const onBrowse = location.pathname === '/';
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (onBrowse) return;
    if (secondsLeft === 0) {
      navigate('/', { replace: true });
      return;
    }
    const id = setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => clearTimeout(id);
  }, [onBrowse, navigate, secondsLeft]);

  let title = 'Something went wrong';
  let message = 'An unexpected error occurred. Please try again.';

  if (isRouteErrorResponse(error)) {
    title = `${error.status} ${error.statusText}`;
    if (typeof error.data === 'string') {
      message = error.data;
    } else if (
      error.data !== null &&
      typeof error.data === 'object' &&
      'message' in error.data &&
      typeof (error.data as { message: unknown }).message === 'string'
    ) {
      message = (error.data as { message: string }).message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <ErrorPanel
      title={title}
      message={message}
      actions={
        <>
          <Button type="button" variant="destructive" onClick={() => navigate(0)}>
            Retry
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/')}>
            Back to browse
          </Button>
        </>
      }
    >
      {!onBrowse && (
        <p className="text-text-2 text-small mt-2" aria-live="polite">
          Returning to Browse in {secondsLeft}s…
        </p>
      )}
    </ErrorPanel>
  );
}
